import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FARM_CONTEXT_STORAGE_KEY } from '../constants/farmContext';
import { isGlobalRole } from '../utils/roles';
import { authFetch } from '../utils/authFetch';
import { apiUrl } from '../utils/apiUrl';
import { useAuth } from './AuthContext';

const FarmContext = createContext({
  effectiveFarmId: null,
  contextOverrideId: null,
  setFarmContextId: () => {},
  farms: [],
  farmPlan: null,
});

export function FarmProvider({ children }) {
  const { token, user, realUser, ready } = useAuth();
  const effectiveAuthUser = realUser || user;
  const [fallbackFarms, setFallbackFarms] = useState([]);
  const [farmPlan, setFarmPlan] = useState(null);
  const [overrideId, setOverrideId] = useState(() => {
    try {
      return localStorage.getItem(FARM_CONTEXT_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!ready || !effectiveAuthUser) return;
    if (!isGlobalRole(effectiveAuthUser.role)) {
      setOverrideId(null);
      try {
        localStorage.removeItem(FARM_CONTEXT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [ready, effectiveAuthUser]);

  useEffect(() => {
    if (!ready || !token || !isGlobalRole(effectiveAuthUser?.role)) {
      setFallbackFarms([]);
      return;
    }
    const authFarms = effectiveAuthUser?.farms;
    if (Array.isArray(authFarms) && authFarms.length > 0) {
      setFallbackFarms([]);
      return;
    }
    let cancelled = false;
    fetch(apiUrl('/api/farms'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const normalized = Array.isArray(list)
          ? list.map((f) => ({ id: String(f.id || f._id || ''), name: f.name || '' })).filter((f) => f.id && f.name)
          : [];
        setFallbackFarms(normalized);
      })
      .catch(() => {
        if (!cancelled) setFallbackFarms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, token, effectiveAuthUser?.role, effectiveAuthUser?.farms]);

  const setFarmContextId = useCallback((id) => {
    if (id) {
      localStorage.setItem(FARM_CONTEXT_STORAGE_KEY, String(id));
      setOverrideId(String(id));
    } else {
      localStorage.removeItem(FARM_CONTEXT_STORAGE_KEY);
      setOverrideId(null);
    }
  }, []);

  const effectiveFarmId = useMemo(() => {
    const baseFarmId = user?.farmId || effectiveAuthUser?.farmId || null;
    if (!baseFarmId) return null;
    if (isGlobalRole(effectiveAuthUser?.role) && overrideId) return overrideId;
    return baseFarmId;
  }, [user, effectiveAuthUser, overrideId]);

  const farms = useMemo(() => {
    const source = effectiveAuthUser?.farms || user?.farms || [];
    if (Array.isArray(source) && source.length > 0) {
      return source.map((f) => ({ ...f, id: String(f.id || f._id || '') })).filter((f) => f.id);
    }
    return fallbackFarms;
  }, [effectiveAuthUser?.farms, user?.farms, fallbackFarms]);

  useEffect(() => {
    if (!ready || !token || !effectiveFarmId) {
      setFarmPlan(null);
      return;
    }
    let cancelled = false;
    authFetch('/api/farms/current/plan', {}, { token, farmContextId: effectiveFarmId })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (cancelled) return;
        if (payload && typeof payload === 'object') {
          setFarmPlan({
            planType: payload.planType || 'included',
            historyLimitDays: Number(payload.historyLimitDays) || 1,
            features: {
              vpdAnalysis: payload.features?.vpdAnalysis === true,
            },
            farmName: payload.farmName ?? null,
          });
        } else {
          setFarmPlan({
            planType: 'included',
            historyLimitDays: 1,
            features: { vpdAnalysis: false },
            farmName: null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFarmPlan({
            planType: 'included',
            historyLimitDays: 1,
            features: { vpdAnalysis: false },
            farmName: null,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, token, effectiveFarmId]);

  const value = useMemo(
    () => ({
      effectiveFarmId,
      contextOverrideId: isGlobalRole(effectiveAuthUser?.role) ? overrideId : null,
      setFarmContextId,
      farms,
      farmName: user?.farmName || effectiveAuthUser?.farmName || null,
      farmPlan,
    }),
    [
      effectiveFarmId,
      overrideId,
      setFarmContextId,
      farms,
      user?.farmName,
      effectiveAuthUser?.farmName,
      effectiveAuthUser?.role,
      farmPlan,
    ]
  );

  return <FarmContext.Provider value={value}>{children}</FarmContext.Provider>;
}

export function useFarm() {
  return useContext(FarmContext);
}
